/**
 * Unified Canvas Element Discovery System
 * Handles automatic element discovery with fallback support and timing management
 * for all project types with robust canvas rendering validation
 */

import { ProjectCanvasConfig, DiscoveredElements } from '../types/ProjectCanvasConfig';
import { logDebug, logError, LogTag } from '../utils/utils';

export interface CanvasValidationResult {
    isValid: boolean;
    width: number;
    height: number;
    hasContent: boolean;
    errorMessage?: string;
}

export interface ElementDiscoveryOptions {
    /** Timeout for waiting for elements to appear */
    waitTimeout?: number;
    /** Whether to validate canvas content before considering it found */
    validateContent?: boolean;
    /** Whether to wait for canvas rendering completion */
    waitForRendering?: boolean;
    /** Additional wait time after element is found */
    stabilizationDelay?: number;
}

class CanvasElementDiscovery {
    // TASK 2: Centralized timeout configuration by project type
    private static readonly PROJECT_TIMEOUT_CONFIG = {
        airport: {
            discovery: 400, // Optimized for simple airport projects (was 800ms)
            stabilization: 50, // Fast stabilization (was 100ms)
            enableOptimizations: true,
        },
        paved: {
            discovery: 600, // Balanced for AASHTO complexity (was 1000ms)
            stabilization: 75, // Medium stabilization (was 100ms)
            enableOptimizations: true,
        },
        unpaved: {
            discovery: 800, // Allow for geotextile complexity (was 2500ms)
            stabilization: 100, // Standard stabilization (unchanged)
            enableOptimizations: true,
        },
        container_yard: {
            discovery: 700, // Legacy compatibility (was 1000ms)
            stabilization: 85, // Slightly reduced (was 100ms)
            enableOptimizations: true,
        },
        geogrid: {
            discovery: 1200, // Konva rendering complexity (was 2500ms)
            stabilization: 150, // Extra stabilization for Konva (was 100ms)
            enableOptimizations: true,
        },
        railways: {
            discovery: 1000, // Medium complexity (was 2500ms)
            stabilization: 120, // Enhanced stabilization (was 100ms)
            enableOptimizations: true,
        },
    };

    // Feature flag for rollback capability
    private static isOptimizedTimeoutsEnabled(): boolean {
        return process.env.REACT_APP_ENABLE_OPTIMIZED_TIMEOUTS !== 'false';
    }

    // Environment detection for timeout scaling
    private static isRemoteDomain(): boolean {
        if (typeof window === 'undefined') return false;

        const hostname = window.location.hostname.toLowerCase();
        const remoteDomains = ['mactest.netkom.it', 'macread.netkom.it', 'maccaferri-app.com', 'netkom.it'];

        return remoteDomains.some(domain => hostname.includes(domain) || hostname.endsWith(domain));
    }

    // Get environment-aware timeout multiplier
    private static getTimeoutMultiplier(): number {
        if (this.isRemoteDomain()) {
            return 3.0; // Triple timeouts for remote domains with latency
        }
        return 1.0; // Normal timeouts for local development
    }

    private static readonly DEFAULT_OPTIONS: Required<ElementDiscoveryOptions> = {
        waitTimeout: 800, // TASK 2: Updated default (was 1500ms)
        validateContent: false,
        waitForRendering: true,
        stabilizationDelay: 90, // TASK 2: Updated default (was 100ms)
    };

    /**
     * Get optimal timeout based on project type and element type with remote domain scaling
     * @param projectType The type of project (unpaved, container_yard, etc.)
     * @param elementId The element ID being discovered
     * @param elementType The type of element (canvas, svg, etc.)
     * @returns timeout in milliseconds
     */
    private static getOptimalTimeout(projectType: string, elementId?: string, elementType?: string): number {
        const timeoutConfig = this.getTimeoutConfig(projectType);
        const timeoutMultiplier = this.getTimeoutMultiplier();

        // Use centralized configuration if optimizations are enabled
        if (timeoutConfig.enableOptimizations) {
            // Special handling for complex elements that need extra time
            if (elementId?.includes('geotextile') || elementId?.includes('konva') || elementType === 'konva') {
                // Complex elements get 150% of base timeout, then multiplied for remote domains
                return Math.floor(timeoutConfig.discovery * 1.5 * timeoutMultiplier);
            }

            return Math.floor(timeoutConfig.discovery * timeoutMultiplier);
        }

        // Legacy fallback logic when optimizations are disabled
        let baseTimeout: number;
        if (projectType === 'airport') {
            baseTimeout = 800;
        } else if (elementId?.includes('geotextile') || projectType === 'geogrid' || projectType === 'railways') {
            baseTimeout = 2500;
        } else if (elementId?.includes('konva') || elementType === 'konva') {
            baseTimeout = 1500;
        } else {
            baseTimeout = 1000;
        }

        return Math.floor(baseTimeout * timeoutMultiplier);
    }
    /**
     * Discover canvas elements based on project configuration
     * @param config Project canvas configuration
     * @param options Discovery options
     * @returns Promise with discovered elements and metadata
     */
    public static async discoverElements(config: ProjectCanvasConfig, options: ElementDiscoveryOptions = {}): Promise<DiscoveredElements> {
        const opts = { ...this.DEFAULT_OPTIONS, ...options };

        logDebug('CanvasElementDiscovery', `Starting element discovery for project: ${config.projectType}`, undefined, LogTag.PDF_IMAGES);

        const discoveredElements: HTMLCanvasElement[] = [];
        const foundIds: string[] = [];
        const missingIds: string[] = [];

        try {
            // OPTIMIZATION: Parallel element discovery instead of sequential
            const discoveryTasks = [
                {
                    promise: this.discoverSingleElement(config.canvasElements.primary, config, opts, 'primary', options),
                    elementId: config.canvasElements.primary,
                    type: 'primary',
                },
                {
                    promise: this.discoverSingleElement(config.canvasElements.secondary, config, opts, 'secondary', options),
                    elementId: config.canvasElements.secondary,
                    type: 'secondary',
                },
            ];

            // Add tertiary element if specified
            if (config.canvasElements.tertiary) {
                discoveryTasks.push({
                    promise: this.discoverSingleElement(config.canvasElements.tertiary, config, opts, 'tertiary', options),
                    elementId: config.canvasElements.tertiary,
                    type: 'tertiary',
                });
            }

            logDebug('CanvasElementDiscovery', `Starting parallel discovery of ${discoveryTasks.length} elements for ${config.projectType}`, undefined, LogTag.PDF_IMAGES);

            // Execute all discovery tasks in parallel
            const results = await Promise.allSettled(discoveryTasks.map(task => task.promise));

            // Process results and build discovery data
            results.forEach((result, index) => {
                const task = discoveryTasks[index];

                if (result.status === 'fulfilled' && result.value) {
                    discoveredElements.push(result.value);
                    foundIds.push(task.elementId);
                    logDebug('CanvasElementDiscovery', `Parallel discovery success: ${task.type} element (${task.elementId})`, undefined, LogTag.PDF_IMAGES);
                } else {
                    missingIds.push(task.elementId);
                    const errorMsg = result.status === 'rejected' ? result.reason : 'Element not found';
                    logDebug('CanvasElementDiscovery', `Parallel discovery failed: ${task.type} element (${task.elementId}) - ${errorMsg}`, undefined, LogTag.PDF_IMAGES);
                }
            });

            // Special handling for geogrid canvas with enhanced timing
            if (config.specialHandling.hasGeogridCanvas && discoveredElements.length > 0) {
                await this.waitForGeogridCanvasReady(discoveredElements, config.timingConfig.canvasWaitTime);
            }

            const allFound = missingIds.length === 0;
            const result: DiscoveredElements = {
                elements: discoveredElements,
                foundIds,
                missingIds,
                allFound,
            };

            logDebug('CanvasElementDiscovery', `Discovery completed. Found: ${foundIds.length}, Missing: ${missingIds.length}`, undefined, LogTag.PDF_IMAGES);

            return result;
        } catch (error) {
            logError('CanvasElementDiscovery', 'Element discovery failed:', error, LogTag.PDF_IMAGES);
            throw new Error(`Canvas element discovery failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    // TASK 2: Get optimized timeout configuration by project type
    private static getTimeoutConfig(projectType?: string): { discovery: number; stabilization: number; enableOptimizations: boolean } {
        if (!CanvasElementDiscovery.isOptimizedTimeoutsEnabled()) {
            // Fallback to legacy values when feature flag is disabled
            return {
                discovery: 1500,
                stabilization: 100,
                enableOptimizations: false,
            };
        }

        // Normalize project type for lookup
        const normalizedType = projectType?.toLowerCase() || 'paved';

        // Map project type to configuration
        const typeMapping: Record<string, keyof typeof CanvasElementDiscovery.PROJECT_TIMEOUT_CONFIG> = {
            airport: 'airport',
            paved: 'paved',
            unpaved: 'unpaved',
            container_yard: 'container_yard',
            geogrid: 'geogrid',
            konva: 'geogrid', // Konva maps to geogrid config
            railways: 'railways',
            railway: 'railways', // Alternative naming
        };

        const configKey = typeMapping[normalizedType] || 'paved';
        const config = CanvasElementDiscovery.PROJECT_TIMEOUT_CONFIG[configKey];

        return config;
    }

    // TASK 3: Smart Content Validation System
    /**
     * Determine if a project type is simple and doesn't require content validation
     * @param projectType Project type to evaluate
     * @returns true if project is simple and can skip validation
     */
    private static isSimpleProject(projectType: string): boolean {
        const simpleProjects = [
            'airport', // Predictable content, no complex rendering
            'container_yard', // Legacy, stable canvas content
        ];

        return simpleProjects.includes(projectType.toLowerCase());
    }

    /**
     * Determine if content validation is required based on project complexity
     * @param projectType Project type
     * @param config Project configuration
     * @param elementId Element ID being validated
     * @returns true if validation should be performed
     */
    private static shouldValidateContent(projectType: string, config: ProjectCanvasConfig, elementId?: string): boolean {
        // Skip validation for simple projects
        if (this.isSimpleProject(projectType)) {
            return false;
        }

        // Always validate geotextile elements due to complexity
        if (elementId?.includes('geotextile')) {
            return true;
        }

        // Use configuration setting for other cases
        return config.specialHandling.validateCanvasContent;
    }

    /**
     * Discover a single canvas element with fallback support
     * @param elementId Primary element ID to search for
     * @param config Project configuration for fallback options
     * @param options Discovery options
     * @param elementType Type of element (for logging)
     * @returns Canvas element or null if not found
     */
    private static async discoverSingleElement(
        elementId: string,
        config: ProjectCanvasConfig,
        options: Required<ElementDiscoveryOptions>,
        elementType: string,
        originalOptions: ElementDiscoveryOptions = {}
    ): Promise<HTMLCanvasElement | null> {
        logDebug('CanvasElementDiscovery', `Discovering ${elementType} element: ${elementId}`, undefined, LogTag.PDF_IMAGES);

        // Apply selective timeout only if no explicit timeout was provided
        const isDefaultTimeout = !originalOptions.waitTimeout; // User didn't specify custom timeout

        const optimizedTimeout = this.getOptimalTimeout(config.projectType, elementId, elementType);
        const effectiveTimeout = isDefaultTimeout ? optimizedTimeout : options.waitTimeout;

        logDebug(
            'CanvasElementDiscovery',
            `Using timeout: ${effectiveTimeout}ms for ${elementType} element (${elementId}) [${isDefaultTimeout ? 'optimized' : 'explicit'}]`,
            undefined,
            LogTag.PDF_IMAGES
        );

        // Generate fallback IDs based on project type patterns
        const fallbackIds = this.generateFallbackIds(elementId, config);
        const allIds = [elementId, ...fallbackIds];

        logDebug('CanvasElementDiscovery', `Parallel discovery attempting IDs: ${allIds.join(', ')} for ${elementType}`, undefined, LogTag.PDF_IMAGES);

        // OPTIMIZATION: Try parallel discovery first, fallback to sequential for compatibility
        // This maintains backward compatibility while enabling performance improvements
        for (const id of allIds) {
            try {
                // Prioritize primary ID with shorter timeout, longer timeout for fallbacks
                const timeout = id === elementId ? effectiveTimeout : Math.floor(effectiveTimeout * 0.7);

                // Wait for element to appear
                const element = await this.waitForElement(id, timeout);

                if (element) {
                    // Find canvas within the element
                    const canvas = this.extractCanvas(element);

                    if (canvas) {
                        // TASK 3: Smart validation - only validate when necessary
                        const shouldValidate = this.shouldValidateContent(config.projectType, config, id) && options.validateContent;

                        if (shouldValidate) {
                            const validation = await this.validateCanvas(canvas, config, options, id);
                            if (!validation.isValid) {
                                logDebug('CanvasElementDiscovery', `Canvas ${id} validation failed: ${validation.errorMessage}`, undefined, LogTag.PDF_IMAGES);
                                continue;
                            }
                            logDebug('CanvasElementDiscovery', `Canvas ${id} validation passed for ${config.projectType}`, undefined, LogTag.PDF_IMAGES);
                        } else {
                            logDebug('CanvasElementDiscovery', `Skipping validation for ${id} in ${config.projectType} (simple project or validation not required)`, undefined, LogTag.PDF_IMAGES);
                        }

                        // Wait for rendering completion if required
                        if (options.waitForRendering) {
                            // Special handling for geotextile canvas - ensure it's fully rendered
                            if (elementType === 'tertiary' && config.projectType === 'unpaved' && id === 'tire-layer-geotextile') {
                                await this.waitForGeotextileCanvasReady(canvas, config.timingConfig.canvasWaitTime);
                            } else {
                                await this.waitForCanvasReady(canvas, config.timingConfig.canvasWaitTime);
                            }
                        }

                        // TASK 2: Use centralized stabilization delay configuration
                        const timeoutConfig = this.getTimeoutConfig(config.projectType);
                        const stabilizationDelay =
                            originalOptions.stabilizationDelay !== undefined
                                ? originalOptions.stabilizationDelay // User explicitly set delay
                                : timeoutConfig.stabilization; // Use optimized configuration

                        if (stabilizationDelay > 0) {
                            logDebug('CanvasElementDiscovery', `Applying stabilization delay: ${stabilizationDelay}ms for ${elementType} (${config.projectType})`, undefined, LogTag.PDF_IMAGES);
                            await this.delay(stabilizationDelay);
                        }

                        logDebug('CanvasElementDiscovery', `Successfully discovered ${elementType} canvas: ${id}`, undefined, LogTag.PDF_IMAGES);
                        return canvas;
                    }
                }
            } catch (error) {
                logDebug('CanvasElementDiscovery', `Failed to discover element ${id}:`, error, LogTag.PDF_IMAGES);
                continue;
            }
        }

        logDebug('CanvasElementDiscovery', `${elementType} element not found. Tried IDs: ${allIds.join(', ')}`, undefined, LogTag.PDF_IMAGES);
        return null;
    }

    /**
     * Generate intelligent fallback element IDs based on project patterns and probability
     * OPTIMIZATION: Smart fallback ordering based on project type for faster discovery
     * @param primaryId Primary element ID
     * @param config Project configuration
     * @returns Array of fallback IDs ordered by probability of success
     */
    private static generateFallbackIds(primaryId: string, config: ProjectCanvasConfig): string[] {
        const fallbacks: string[] = [];

        // Project-specific optimized fallback mappings with probability ordering
        const projectOptimizedMappings: Record<string, Record<string, string[]>> = {
            // Airport projects - high probability mappings first
            airport: {
                'tire-layer-unstabilized': ['tire-layer-unreinforced'],
                'tire-layer-increased-passages': ['tire-layer-reinforced', 'tire-layer-geogrid'],
                'tire-layer-geogrid': ['tire-layer-reinforced', 'tire-layer-increased-passages'],
            },
            // Paved projects - optimized for AASHTO patterns
            paved: {
                'tire-layer-unstabilized': ['tire-layer-unreinforced'],
                'tire-layer-increased-passages': ['tire-layer-reinforced', 'tire-layer-geogrid'],
                'tire-layer-geogrid': ['tire-layer-reinforced', 'tire-layer-increased-passages'],
            },
            // Unpaved projects - includes geotextile patterns
            unpaved: {
                'tire-layer-unstabilized': ['tire-layer-unreinforced'],
                'tire-layer-geogrid': ['tire-layer-reinforced', 'tire-layer-geotextile'],
                'tire-layer-geotextile': ['tire-layer-geogrid', 'tire-layer-reinforced'],
                'tire-layer-reinforced': ['tire-layer-geogrid', 'tire-layer-geotextile'],
            },
            // Container yard - legacy patterns
            container_yard: {
                'tire-layer-unreinforced': ['tire-layer-unstabilized'],
                'tire-layer-reinforced': ['tire-layer-geogrid', 'tire-layer-stabilized'],
                'tire-layer-geogrid': ['tire-layer-reinforced'],
            },
        };

        // Get project-specific mappings if available
        const projectMappings = projectOptimizedMappings[config.projectType] || {};

        if (projectMappings[primaryId]) {
            fallbacks.push(...projectMappings[primaryId]);
        } else {
            // Fallback to general mappings if project-specific not found
            const generalMappings: Record<string, string[]> = {
                'tire-layer-unreinforced': ['tire-layer-unstabilized'],
                'tire-layer-unstabilized': ['tire-layer-unreinforced'],
                'tire-layer-reinforced': ['tire-layer-increased-passages', 'tire-layer-geogrid'],
                'tire-layer-increased-passages': ['tire-layer-reinforced', 'tire-layer-geogrid'],
                'tire-layer-geogrid': ['tire-layer-reinforced', 'tire-layer-increased-passages'],
                'tire-layer-geotextile': ['tire-layer-geogrid'],
            };

            if (generalMappings[primaryId]) {
                fallbacks.push(...generalMappings[primaryId]);
            }
        }

        logDebug('CanvasElementDiscovery', `Generated ${fallbacks.length} fallback IDs for ${primaryId} in ${config.projectType}: ${fallbacks.join(', ')}`, undefined, LogTag.PDF_IMAGES);
        return fallbacks;
    }

    /**
     * TASK 4: Event-driven element discovery with optimized MutationObserver
     * @param elementId Element ID to wait for
     * @param timeout Maximum wait time in milliseconds
     * @returns Promise that resolves with the element or null
     */
    private static async waitForElement(elementId: string, timeout: number): Promise<HTMLElement | null> {
        return new Promise(resolve => {
            // Immediate check first
            const element = document.getElementById(elementId);
            if (element) {
                resolve(element);
                return;
            }

            // TASK 4: Event-driven approach with Promise.race
            let isResolved = false;
            const resolveOnce = (element: HTMLElement | null) => {
                if (!isResolved) {
                    isResolved = true;
                    resolve(element);
                }
            };

            // TASK 4: Primary MutationObserver approach
            const observerPromise = this.createMutationObserver(elementId, timeout);

            // TASK 4: Reduced fast polling as backup (3 attempts, 200ms intervals)
            const pollingPromise = this.createReducedPolling(elementId, timeout);

            // TASK 4: Promise.race for immediate response
            Promise.race([observerPromise, pollingPromise])
                .then(resolveOnce)
                .catch(() => resolveOnce(null));

            // Final timeout fallback
            setTimeout(() => resolveOnce(null), timeout);
        });
    }

    /**
     * TASK 4: Create optimized MutationObserver for element discovery
     * @param elementId Target element ID
     * @param timeout Maximum observation time
     * @returns Promise that resolves when element is found
     */
    private static createMutationObserver(elementId: string, timeout: number): Promise<HTMLElement | null> {
        return new Promise((resolve, reject) => {
            let observer: MutationObserver | null = null;
            let debounceTimer: NodeJS.Timeout | null = null;
            let isCompleted = false;

            const cleanup = () => {
                if (observer) {
                    observer.disconnect();
                    observer = null;
                }
                if (debounceTimer) {
                    clearTimeout(debounceTimer);
                    debounceTimer = null;
                }
                isCompleted = true;
            };

            const checkElement = () => {
                if (isCompleted) return;

                const element = document.getElementById(elementId);
                if (element) {
                    cleanup();
                    resolve(element);
                }
            };

            // TASK 4: Debounced observation to avoid excessive DOM checks
            const debouncedCheck = () => {
                if (debounceTimer) {
                    clearTimeout(debounceTimer);
                }
                debounceTimer = setTimeout(checkElement, 50); // 50ms debounce
            };

            try {
                // TASK 4: Check immediately if element already exists
                const existingElement = document.getElementById(elementId);
                if (existingElement) {
                    cleanup();
                    resolve(existingElement);
                    return;
                }

                // TASK 4: DOM readiness detection
                const startObserver = () => {
                    if (isCompleted) return;

                    observer = new MutationObserver(mutations => {
                        // TASK 4: Optimized mutation filtering
                        let shouldCheck = false;
                        for (const mutation of mutations) {
                            if (mutation.type === 'childList') {
                                // Check if added nodes contain our target
                                const addedNodes = Array.from(mutation.addedNodes);
                                for (const addedNode of addedNodes) {
                                    if (addedNode.nodeType === Node.ELEMENT_NODE) {
                                        const element = addedNode as Element;
                                        if (element.id === elementId || element.querySelector(`#${elementId}`)) {
                                            shouldCheck = true;
                                            break;
                                        }
                                    }
                                }
                                if (shouldCheck) break;
                            }
                        }

                        if (shouldCheck) {
                            debouncedCheck();
                        }
                    });

                    observer.observe(document.body, {
                        childList: true,
                        subtree: true,
                        // TASK 4: Optimized observation options
                        attributes: false, // We only care about structure changes
                        characterData: false,
                    });

                    logDebug('CanvasElementDiscovery', `MutationObserver started for element: ${elementId}`, undefined, LogTag.PDF_IMAGES);
                };

                if (document.readyState === 'loading') {
                    // Wait for DOM ready before starting observation
                    document.addEventListener(
                        'DOMContentLoaded',
                        () => {
                            if (!isCompleted) {
                                startObserver();
                            }
                        },
                        { once: true }
                    );
                } else {
                    startObserver();
                }

                // Cleanup timeout
                setTimeout(() => {
                    if (!isCompleted) {
                        cleanup();
                        reject(new Error('MutationObserver timeout'));
                    }
                }, timeout);
            } catch (error) {
                cleanup();
                reject(error);
            }
        });
    }

    /**
     * TASK 4: Reduced polling as fallback mechanism
     * @param elementId Target element ID
     * @param timeout Maximum polling time
     * @returns Promise that resolves when element is found
     */
    private static createReducedPolling(elementId: string, timeout: number): Promise<HTMLElement | null> {
        return new Promise((resolve, reject) => {
            let pollCount = 0;
            const maxPolls = 3; // TASK 4: Reduced from 10 to 3 attempts
            const pollInterval = 200; // TASK 4: Increased from 100ms to 200ms

            const poll = setInterval(() => {
                const element = document.getElementById(elementId);
                if (element) {
                    clearInterval(poll);
                    resolve(element);
                    return;
                }

                pollCount++;
                if (pollCount >= maxPolls) {
                    clearInterval(poll);
                    reject(new Error('Polling timeout'));
                }
            }, pollInterval);

            // Cleanup timeout
            setTimeout(() => {
                clearInterval(poll);
                reject(new Error('Polling timeout'));
            }, timeout);
        });
    }

    /**
     * Extract canvas element from a container element
     * @param element Container element
     * @returns Canvas element or null
     */
    private static extractCanvas(element: HTMLElement): HTMLCanvasElement | null {
        // If the element itself is a canvas
        if (element.tagName.toLowerCase() === 'canvas') {
            return element as HTMLCanvasElement;
        }

        // Look for canvas within the element
        const canvas = element.querySelector('canvas');
        return canvas as HTMLCanvasElement | null;
    }

    /**
     * Validate canvas element and its content
     * @param canvas Canvas element to validate
     * @param config Project configuration
     * @param options Discovery options
     * @param elementId Optional element ID for enhanced validation logic
     * @returns Validation result
     */
    private static async validateCanvas(canvas: HTMLCanvasElement, config: ProjectCanvasConfig, _options: Required<ElementDiscoveryOptions>, _elementId?: string): Promise<CanvasValidationResult> {
        try {
            // TASK 3: Fast-path validation for simple projects
            if (this.isSimpleProject(config.projectType)) {
                const width = canvas.width;
                const height = canvas.height;

                // Basic dimension check is sufficient for simple projects
                if (width > 0 && height > 0) {
                    logDebug(
                        'CanvasElementDiscovery',
                        `Fast-path validation for simple project ${config.projectType}:`,
                        {
                            canvasId: canvas.id,
                            dimensions: `${width}x${height}`,
                            fastPath: true,
                        },
                        LogTag.PDF_IMAGES
                    );
                    return {
                        isValid: true,
                        width,
                        height,
                        hasContent: true, // Assume content for simple projects
                    };
                } else {
                    return {
                        isValid: false,
                        width,
                        height,
                        hasContent: false,
                        errorMessage: `Invalid canvas dimensions: ${width}x${height}`,
                    };
                }
            }

            // Standard validation path for complex projects
            const width = canvas.width;
            const height = canvas.height;

            if (width <= 0 || height <= 0) {
                logDebug(
                    'CanvasElementDiscovery',
                    `Canvas validation failed - invalid dimensions: ${width}x${height}`,
                    {
                        canvasId: canvas.id,
                        className: canvas.className,
                        isConnected: canvas.isConnected,
                    },
                    LogTag.PDF_IMAGES
                );
                return {
                    isValid: false,
                    width,
                    height,
                    hasContent: false,
                    errorMessage: `Invalid canvas dimensions: ${width}x${height}`,
                };
            }

            // Special validation for geotextile canvas (tertiary canvas in unpaved projects)
            if (config.projectType === 'unpaved' && canvas.id === 'tire-layer-geotextile') {
                const geotextileValidation = await this.validateGeotextileCanvas(canvas);
                logDebug(
                    'CanvasElementDiscovery',
                    'Geotextile canvas specific validation result:',
                    {
                        canvasId: canvas.id,
                        dimensions: `${geotextileValidation.width}x${geotextileValidation.height}`,
                        hasContent: geotextileValidation.hasContent,
                        isValid: geotextileValidation.isValid,
                        errorMessage: geotextileValidation.errorMessage,
                    },
                    LogTag.PDF_IMAGES
                );
                return geotextileValidation;
            }

            // Check if canvas has content (for validation when required)
            let hasContent = true;
            if (config.specialHandling.validateCanvasContent) {
                hasContent = await this.checkCanvasContent(canvas);
                logDebug(
                    'CanvasElementDiscovery',
                    'Canvas content validation:',
                    {
                        canvasId: canvas.id,
                        hasContent,
                        contentValidationRequired: true,
                    },
                    LogTag.PDF_IMAGES
                );
            }

            const result = {
                isValid: true,
                width,
                height,
                hasContent,
            };

            logDebug(
                'CanvasElementDiscovery',
                'Canvas validation successful:',
                {
                    canvasId: canvas.id,
                    dimensions: `${width}x${height}`,
                    hasContent,
                    isConnected: canvas.isConnected,
                },
                LogTag.PDF_IMAGES
            );

            return result;
        } catch (error) {
            logError(
                'CanvasElementDiscovery',
                'Canvas validation error:',
                {
                    canvasId: canvas.id,
                    error: error instanceof Error ? error.message : String(error),
                },
                LogTag.PDF_IMAGES
            );
            return {
                isValid: false,
                width: 0,
                height: 0,
                hasContent: false,
                errorMessage: `Canvas validation error: ${error instanceof Error ? error.message : String(error)}`,
            };
        }
    }

    /**
     * Enhanced validation specifically for geotextile canvas elements
     * @param canvas Geotextile canvas element to validate
     * @returns Promise with validation result
     */
    private static async validateGeotextileCanvas(canvas: HTMLCanvasElement): Promise<CanvasValidationResult> {
        try {
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return {
                    isValid: false,
                    width: 0,
                    height: 0,
                    hasContent: false,
                    errorMessage: 'No 2D context available for geotextile canvas',
                };
            }

            const width = canvas.width;
            const height = canvas.height;

            if (width <= 0 || height <= 0) {
                return {
                    isValid: false,
                    width,
                    height,
                    hasContent: false,
                    errorMessage: `Geotextile canvas has invalid dimensions: ${width}x${height}`,
                };
            }

            // Check for actual rendered content in the canvas
            const imageData = ctx.getImageData(0, 0, width, height);
            const data = imageData.data;

            // Count non-transparent pixels
            let nonTransparentPixels = 0;
            for (let i = 3; i < data.length; i += 4) {
                if (data[i] > 0) {
                    // Alpha channel > 0
                    nonTransparentPixels++;
                }
            }

            const totalPixels = width * height;
            const contentPercentage = (nonTransparentPixels / totalPixels) * 100;
            const hasSignificantContent = contentPercentage > 0.1; // At least 0.1% of canvas has content

            logDebug(
                'CanvasElementDiscovery',
                'Geotextile canvas content analysis:',
                {
                    canvasId: canvas.id,
                    dimensions: `${width}x${height}`,
                    totalPixels,
                    nonTransparentPixels,
                    contentPercentage: contentPercentage.toFixed(2) + '%',
                    hasSignificantContent,
                    isConnected: canvas.isConnected,
                    visibility: window.getComputedStyle(canvas).visibility,
                    display: window.getComputedStyle(canvas).display,
                },
                LogTag.PDF_IMAGES
            );

            if (!hasSignificantContent) {
                return {
                    isValid: false,
                    width,
                    height,
                    hasContent: false,
                    errorMessage: `Geotextile canvas appears empty or transparent (only ${contentPercentage.toFixed(2)}% content)`,
                };
            }

            return {
                isValid: true,
                width,
                height,
                hasContent: true,
            };
        } catch (error) {
            logError(
                'CanvasElementDiscovery',
                'Geotextile canvas validation failed:',
                {
                    canvasId: canvas.id,
                    error: error instanceof Error ? error.message : String(error),
                },
                LogTag.PDF_IMAGES
            );
            return {
                isValid: false,
                width: 0,
                height: 0,
                hasContent: false,
                errorMessage: `Geotextile canvas validation error: ${error instanceof Error ? error.message : String(error)}`,
            };
        }
    }

    /**
     * Check if canvas has actual content drawn on it
     * @param canvas Canvas element to check
     * @returns Promise that resolves to true if canvas has content
     */
    private static async checkCanvasContent(canvas: HTMLCanvasElement): Promise<boolean> {
        try {
            const context = canvas.getContext('2d');
            if (!context) return false;

            // Get canvas image data
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // Check if there are any non-transparent pixels
            for (let i = 3; i < data.length; i += 4) {
                if (data[i] > 0) {
                    // Alpha channel > 0
                    return true;
                }
            }

            return false;
        } catch (error) {
            logDebug('CanvasElementDiscovery', 'Content check failed:', error, LogTag.PDF_IMAGES);
            return true; // Assume content exists if we can't check
        }
    }

    /**
     * Wait for canvas to be ready for capture (specific for Konva/complex rendering)
     * @param canvas Canvas element
     * @param waitTime Time to wait in milliseconds
     */
    private static async waitForCanvasReady(canvas: HTMLCanvasElement, waitTime: number): Promise<void> {
        return new Promise(resolve => {
            const checkReady = () => {
                if (canvas.width > 0 && canvas.height > 0) {
                    setTimeout(resolve, waitTime);
                } else {
                    setTimeout(checkReady, 100);
                }
            };
            checkReady();
        });
    }

    /**
     * Enhanced wait for geogrid canvas with Konva-specific handling
     * @param canvases Array of canvas elements
     * @param waitTime Base wait time
     */
    private static async waitForGeogridCanvasReady(canvases: HTMLCanvasElement[], waitTime: number): Promise<void> {
        logDebug('CanvasElementDiscovery', 'Waiting for geogrid canvas rendering completion...', undefined, LogTag.PDF_IMAGES);

        // Check for Konva-specific patterns
        const konvaCanvases = canvases.filter(canvas => {
            const parent = canvas.parentElement;
            return parent && (parent.classList.contains('konvajs-content') || parent.querySelector('.konvajs-content') !== null);
        });

        if (konvaCanvases.length > 0) {
            // Enhanced wait for Konva canvas
            await this.delay(waitTime * 1.5);

            // Additional check for rendering completion
            for (const canvas of konvaCanvases) {
                await this.waitForKonvaStage(canvas);
            }
        } else {
            // Standard wait for regular canvas
            await this.delay(waitTime);
        }
    }

    /**
     * Enhanced wait for geotextile canvas with specific timing validation
     * @param canvas Geotextile canvas element
     * @param waitTime Base wait time for rendering
     */
    private static async waitForGeotextileCanvasReady(canvas: HTMLCanvasElement, waitTime: number): Promise<void> {
        logDebug('CanvasElementDiscovery', 'Waiting for geotextile canvas rendering completion...', undefined, LogTag.PDF_IMAGES);

        // Initial wait for basic canvas setup
        await this.delay(waitTime);

        // TASK 3: Optimized validation - reduced attempts and faster checks
        let attempts = 0;
        const maxAttempts = 3; // Reduced from 5 to 3 attempts
        const checkInterval = 100; // Reduced from 200ms to 100ms

        while (attempts < maxAttempts) {
            try {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const hasContent = imageData.data.some((value, index) => index % 4 === 3 && value > 0);

                    if (hasContent) {
                        logDebug('CanvasElementDiscovery', `Geotextile canvas content ready after ${attempts + 1} attempts (optimized)`, undefined, LogTag.PDF_IMAGES);
                        break;
                    }
                }
            } catch (error) {
                logDebug('CanvasElementDiscovery', `Geotextile canvas content check failed (attempt ${attempts + 1}):`, error, LogTag.PDF_IMAGES);
            }

            attempts++;
            if (attempts < maxAttempts) {
                await this.delay(checkInterval);
            }
        }

        // TASK 3: Reduced final stabilization delay
        await this.delay(200); // Reduced from 300ms to 200ms

        logDebug(
            'CanvasElementDiscovery',
            'Geotextile canvas ready for capture:',
            {
                canvasId: canvas.id,
                finalDimensions: `${canvas.width}x${canvas.height}`,
                attemptsUsed: attempts,
            },
            LogTag.PDF_IMAGES
        );
    }

    /**
     * Wait for Konva stage to complete rendering
     * @param canvas Konva canvas element
     */
    private static async waitForKonvaStage(canvas: HTMLCanvasElement): Promise<void> {
        return new Promise(resolve => {
            // Check if parent has Konva stage
            const parent = canvas.parentElement;
            if (parent && (window as any).Konva) {
                // Try to find Konva stage and wait for draw events
                const stage = (window as any).Konva.stages?.find((stage: any) => stage.content && parent.contains(stage.content));

                if (stage) {
                    stage.on('draw', resolve);
                    setTimeout(resolve, 1000); // Fallback timeout
                } else {
                    setTimeout(resolve, 500);
                }
            } else {
                setTimeout(resolve, 500);
            }
        });
    }

    /**
     * Simple delay utility
     * @param ms Milliseconds to delay
     */
    private static async delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

export default CanvasElementDiscovery;
